<template>

<div class="list-view no-sidebar no-sub-header">

	<div class="detail two-columns" v-if="!editingSupplier && !editingProduct && !loading" id="detail_receive">

		<div class="section" id="section_1">
			<h2>
				<span style="border: 2px solid #444; border-radius: 1em; padding: 0.1em 0.33em 0 0.33em; margin: 0 0.25em 0 0;">1</span>
				Receipt Info
			</h2>
			<div class="two-columns">
				<div>
					<label-required label="Log Book" :condition="entry.ffl_log_book_id"></label-required>
					<select v-model="entry.ffl_log_book_id" :disabled="entryLocked">
						<option value="">Select...</option>
						<option v-for="logbook in logbooks" :value="logbook.ffl_log_book_id">{{ logbook.ffl_log_book_code }} - {{ logbook.description }}</option>
					</select>
				</div>
				<div>
					<label-required label="Date Received" :condition="entry.receipt_date" class="full"></label-required>
					<input type="date" v-model="entry.receipt_date" :disabled="entryLocked" :max="today" style="width: 11em;">
				</div>
			</div>
			<div class="three-one-columns">
				<div>
					<label-required label="Received From" :condition="$fullName(contact)"></label-required>
					<div v-if="!showFFLSearch" class="input-with-control">
						<input type="text" :disabled="entryLocked" v-model="search_suppliers" placeholder="Search suppliers..." @keyup.enter="searchSuppliers('suppliers')">
						<div v-if="!entryLocked" :class="['button', search_suppliers ? 'action' : 'disabled', 'input-go']" @click="searchSuppliers()"><i class="fas fa-search"></i></div>
					</div>
					<ffl-number-search v-if="showFFLSearch" @searchResult="setList" />
				</div>
				<div>
					<button v-if="!entryLocked" class="neutral smaller full-width" style="margin: 0.66em 0 0.33em 0;" @click="showFFLSearch = !showFFLSearch">Search By {{ showFFLSearch ? 'Name' : 'FFL' }}</button>
					<button v-if="!entryLocked" class="neutral smaller full-width" @click="newSupplier()">Create Supplier</button>
				</div>
			</div>
			<div class="search-result bordered">
				<div v-if="loadingSuppliers" style="font-size: 1.5em; margin: 1em 0 0 0; text-align: center;"><i class="fa fa-spinner fa-spin"></i></div>
				<div v-else>
					<ul v-if="suppliers.length > 0" class="search-list">
						<li class="clickable-list-item" v-for="(supplier, index) in suppliers" @click="assignSupplier(supplier)">
							<span>{{ supplier.full_name || $fullName(supplier) }}</span>
							<span>{{ supplier.address_1 }}</span>
							<span>{{ supplier.ffl_number }}</span>
							<span>{{ supplier.city }}, {{ supplier.state }} {{ supplier.zip_code }}</span>
						</li>
						<li v-if="supplierSearchContext != 'customers'"><div class="inline-link" @click="searchCustomers('customers')">Search for '{{ search_suppliers }}' in your customer list?</div></li>
						<li v-else><div class="inline-link" @click="searchSuppliers('suppliers')">Search for '{{ search_suppliers }}' in your supplier list?</div></li>
					</ul>
					<div v-else-if="supplierSearchMessage" class="center">
						<div v-html="supplierSearchMessage" style="font-size: 1.25em; margin: 1em 0 0.25em 0;"></div>
						<div v-if="supplierSearchContext != 'customers'"><div class="inline-link" @click="searchCustomers('customers')">Search your customer list?</div></div>
						<div v-else><div class="inline-link" @click="searchSuppliers('suppliers')">Search your supplier list?</div></div>
					</div>
					<template v-else>
						<div v-if="contact.client_id == getClient.client_id && contact.contact_id != getClient.contact_id" class="controls" style="position: absolute; bottom: -0.75em; right: 3px;">
							<button :disabled="entryLocked" class="neutral" @click="editingSupplier = true">Edit</button>
						</div>
						{{ $fullName(contact) }}<br>
						{{ contact.address_1 }}<br>
						<template v-if="contact.address_2">{{ contact.address_2 }}<br></template>
						{{ contact.city }} {{ contact.state }} {{ contact.zip_code }}<br>
						<template v-if="contact.ffl_number"><span class="label">FFL:</span> {{ contact.ffl_number }}</template>
						<template v-if="contact.drivers_license_number"><span class="label">DL:</span> {{ contact.drivers_license_number }}</template>
					</template>
				</div>
			</div>

			<div v-if="fflTransactionInfoRequired" style="margin: 1em 0 0 0;">
				<h2>Additional Transaction Info</h2>
				<div class="checkbox-group" v-if="!entryLocked">
					<span :class="['pointer', 'far', !entry.transaction_info_required ? 'fa-check-square' : 'fa-square']" @click="entry.transaction_info_required = !entry.transaction_info_required"></span>
					<label>Check if additional transaction info is <b>NOT</b> required with this receipt.</label>
				</div>
				<div v-if="entry.transaction_info_required" class="two-columns">
					<div>
						<label>Firearm Dealer Permit Number (if applicable)</label>
						<input type="text" v-model="entry.state_permit_number">
					</div>
					<div>
						<label-required label="Employee Completing Transaction" :condition="entry.employee_name"></label-required>
						<input type="text" v-model="entry.employee_name">
					</div>
				</div>

			</div>

		</div>

		<div class="section" id="section_2">
			<h2>
				<span style="border: 2px solid #444; border-radius: 1em; padding: 0.1em 0.33em 0 0.33em; margin: 0 0.25em 0 0;">2</span>
				Inventory Details
				<span v-if="sectionsComplete < 1" class="complete-first">complete Receipt Info first</span>
			</h2>
			<div class="checkbox-group" v-if="!entryLocked">
				<span :class="['pointer', 'far', is_transfer ? 'fa-check-square' : 'fa-square']" @click="is_transfer = !is_transfer"></span>
				<label class="bold">This is a transfer or pre-sold warehouse item - do not receive as inventory</label>
			</div>
			<div class="two-columns">
				<div class="checkbox-group" v-if="!is_transfer">
					<span :class="['pointer', 'far', product_detail.no_upc ? 'fa-check-square' : 'fa-square']" @click="toggleNoUpc()"></span>
					<label class="bold">This item does not have a UPC</label>
				</div>
				<div class="checkbox-group">
					<span :class="['pointer', 'far', entry.is_used ? 'fa-check-square' : 'fa-square']" @click="toggleIsUsed()"></span>
					<label class="bold">This is a used item</label>
				</div>
			</div>
			<div v-if="is_transfer">
				<label>UPC search (to populate ATF Details)</label>
				<div class="input-with-control">
					<input :disabled="entryLocked" type="text" v-model="product_detail.upc" placeholder="Scan or enter..." @keyup.enter="getItem()">
					<div :class="['button', product_detail.upc ? 'action' : 'disabled', 'input-go']" v-if="!entryLocked" @click="getItem()"><i class="fas fa-search" v-if="!upcSearching"></i><i class="far fa-compass fa-spin" v-else></i></div>
				</div>
			</div>
			<div v-if="!is_transfer">
				<label-required :label="entryLocked ? 'UPC or Manufacturer and Model search' : 'UPC'" :condition="product_detail.upc || product_detail.no_upc || is_transfer"></label-required>
				<div class="three-one-columns">
					<div class="input-with-control">
						<input :disabled="entryLocked" type="text" v-model="product_detail.upc" placeholder="Scan or enter..." @keyup.enter="getItem()">
						<div :class="['button', product_detail.upc ? 'action' : 'disabled', 'input-go']" v-if="!entryLocked" @click="getItem()"><i class="fas fa-search" v-if="!upcSearching"></i><i class="far fa-compass fa-spin" v-else></i></div>
					</div>
					<button v-if="!entryLocked && !receiveWithoutUPC" :class="[ allowEditProduct ? 'action' : 'disabled' ]" @click="editProduct()">{{ productButtonLabel }} UPC</button>
				</div>
				<!-- <div class="alert input-field-error" v-if="upcSearchMessage">{{ upcSearchMessage }}</div> -->
				<div v-if="!receiveWithoutUPC">
					<label-required label="Description" :condition="product_detail.description"></label-required>
					<input :disabled="entryLocked" type="text" v-model="product_detail.description">
					<div class="five-columns">
						<label>Your Current Cost</label>
						<label-required label="Received Cost" :condition="inventory.received_cost"></label-required>
						<label>Cost Average? <span class="inline-tip fas fa-info-circle" @click="showTip('cost_average')"></span></label>
						<label>Current On Hand</label>
						<label>Reorder Point</label>
						<input :disabled="entryLocked" type="text" v-model="inventory.cost" disabled>
						<input-validation v-model="inventory.received_cost" placeholder="Enter Cost" :rules="['noNull', 'floatOnly']" :disabled="entryLocked" @input="recalculateProfit('received_cost')"/>
						<div class="checkbox-group">
							<div :class="['pointer', 'far', inventory.cost_average ? 'fa-check-square' : 'fa-square']" @click="inventory.cost_average = !inventory.cost_average"></div>
							<label>Yes</label> &nbsp;
							<div :class="['pointer', 'far', !inventory.cost_average ? 'fa-check-square' : 'fa-square']" @click="inventory.cost_average = !inventory.cost_average"></div>
							<label>No</label>
						</div>
						<input type="text" :value="inventory.on_hand" disabled>
						<input :disabled="entryLocked" type="text" v-model="inventory.reorder_point">

						<label>MSRP</label>
						<label-required label="Your POS Price" :condition="inventory.price"></label-required>
						<label>POS Profit %</label>
						<label>Last 30 Days</label>
						<label>Restock Qty</label>
						<input type="text" v-model="product_detail.msrp" @blur="product_detail.msrp = (product_detail.msrp) ? parseFloat(product_detail.msrp).toFixed(2) : null" :disabled="product_detail.client_id != client_id">
						<input :disabled="entryLocked" type="text" v-model.lazy="inventory.price" @change="recalculateProfit('price')">
						<input :disabled="entryLocked" type="text" v-model.lazy="inventory.pos_profit" @change="recalculateProfit('pos_profit')">
						<input type="text" :value="inventory.recent_sales" disabled>
						<input :disabled="entryLocked" type="text" v-model="inventory.order_quantity">

						<label>MAP</label>
						<label-required label="Your Online Price" :condition="inventory.web_price"></label-required>
						<label>Online Profit %</label>
						<label>On Order</label>
						<label></label>
						<input type="text" v-model="product_detail.map" @blur="product_detail.map = (product_detail.map) ? parseFloat(product_detail.map).toFixed(2) : null" :disabled="product_detail.client_id != client_id">
						<input :disabled="entryLocked" type="text" v-model.lazy="inventory.web_price" @change="recalculateProfit('web_price')">
						<input :disabled="entryLocked" type="text" v-model.lazy="inventory.web_profit" @change="recalculateProfit('web_profit')">
						<input type="text" :value="inventory.on_order" disabled>
						<div></div>
					</div>
				</div>
			</div>

			<div class="section_dead" v-if="sectionsComplete < 1"></div>

		</div>

		<div class="section" id="section_3">
			<h2>
				<span style="border: 2px solid #444; border-radius: 1em; padding: 0.1em 0.33em 0 0.33em; margin: 0 0.25em 0 0;">3</span>
				ATF Details
				<span v-if="sectionsComplete < 2" class="complete-first">complete Inventory Details first</span>
			</h2>
			<label-required label="Manufacturer" :condition="entry.manufacturer"></label-required><input type="text" v-model="entry.manufacturer" :disabled="entryLocked">

			<div :class="fflNumberInfo.manufacturer_importer ? 'two-columns' : ''">
				<div>
					<label>Importer</label>
					<input type="text" v-model="entry.importer" :disabled="entryLocked">
				</div>
				<div v-if="fflNumberInfo.manufacturer_importer">
					<label>Country of Manufacture</label>
					<input type="text" v-model="entry.country_of_manufacture" :disabled="entryLocked">
				</div>
			</div>
			<label-required label="Model" :condition="entry.model"></label-required>
			<input type="text" v-model="entry.model" :disabled="entryLocked">
			<div class="one-two-columns">
				<div>
					<label-required label="Type" :condition="entry.gun_type"></label-required>
					<select name="gun_type" v-model="entry.gun_type" :disabled="entryLocked">
						<option value="">Select...</option>
						<option v-for="gun_type in gun_types" :value="gun_type">{{ gun_type }}</option>
					</select>
				</div>
				<div>
					<label-required label="Caliber" :condition="entry.caliber"></label-required>
					<input type="text" v-model="entry.caliber" :disabled="entryLocked">
				</div>
			</div>
			<div class="section_dead" v-if="sectionsComplete < 2"></div>

		</div>

		<div class="section" id="section_4">
			<h2>
				<span style="border: 2px solid #444; border-radius: 1em; padding: 0.1em 0.33em 0 0.33em; margin: 0 0.25em 0 0;">4</span>
				Serial Numbers
				<span v-if="sectionsComplete < 3" class="complete-first">complete ATF Details first</span>
			</h2>
			<label-required label="Enter one or more serial numbers" :condition="serial_numbers[0].serial_number != ''"></label-required>
			<div class="two-columns">
				<div v-for="(serial_number, index) in serial_numbers">
					<input type="text" v-model="serial_number.serial_number" :disabled="entryLocked" @change="addSerialInput()" :ref="'serial_input_' + index">
					<div v-if="fflTransactionInfoRequired && entry.transaction_info_required">
						<label-required label="Finish" :condition="serial_number.finish"></label-required>
						<select v-model="serial_number.finish" :disabled="entryLocked">
							<option value="">Select...</option>
							<option value="black">Black</option>
							<option value="blue_steel">Blue Steel</option>
							<option value="nickel">Nickel</option>
							<option value="stainless_steel">Stainless Steel</option>
							<option value="two_toned">Two-Toned</option>
							<option value="cerakote">Cerakote</option>
							<option value="other">Other</option>
						</select>
					</div>
					<div v-if="serial_number.finish == 'two_toned'" class="two-columns">
						<div>
							<label-required label="Tone 1" :condition="serial_number.finish_two_toned_1"></label-required>
							<input type="text" v-model="serial_number.finish_two_toned_1">
						</div>
						<div>
							<label-required label="Tone 2" :condition="serial_number.finish_two_toned_2"></label-required>
							<input type="text" v-model="serial_number.finish_two_toned_2">
						</div>
					</div>
					<div v-if="serial_number.finish == 'cerakote'">
						<label-required label="Color" :condition="serial_number.finish_cerakote"></label-required>
						<input type="text" v-model="serial_number.finish_cerakote">
					</div>
					<div v-if="serial_number.finish == 'other'">
						<label-required label="Other Finish" :condition="serial_number.finish_other"></label-required>
						<input type="text" v-model="serial_number.finish_other">
					</div>
					<label v-if="sequence_codes[serial_number.serial_number]" style="margin: -0.5em 0 1em 0.25em;">Sequence Number: {{ sequence_codes[serial_number.serial_number] }}</label>
				</div>
			</div>
			<button :class="['spaced', serial_numbers[0].serial_number != '' ? 'action' : 'disabled' ]" @click="removeSerialInput()" v-if="!entryLocked">-</button>
			<div :class="['button', serial_numbers[serial_numbers.length - 1].serial_number.length > 0 ? 'action' : 'disabled' ]" @click="addSerialInput()" v-if="!entryLocked">+</div>
			<div v-if="confirmDuplicatesMessage" style="margin: 1em 0; line-height: 1.5em">
				{{ confirmDuplicatesMessage }} OK to receive duplicates?<br>
				<input type="checkbox" v-model="duplicatesOK"> Yes, receive duplicates
			</div>
			<div class="section_dead" v-if="sectionsComplete < 3"></div>
		</div>

		<div class="section" id="section_5">
			<h2>
				<span style="border: 2px solid #444; border-radius: 1em; padding: 0.1em 0.33em 0 0.33em; margin: 0 0.25em 0 0;">5</span>
				Notes
			</h2>
			<textarea v-model="entry.disposal_notes" :disabled="entryLocked"></textarea>
			<div class="section_dead" v-if="sectionsComplete < 4"></div>
		</div>

		<div class="section">
			<div v-if="!saving" :class="['button', 'spaced', sectionsComplete < 4 || saveComplete ? 'disabled' : 'action' ]" @click="confirmDuplicates()">{{ entryLocked ? 'Inventory' : 'Receive' }} {{ countSerialNumbers }} Item{{ countSerialNumbers == 1 ? '' : 's' }}</div>
			<div v-else :class="['button', 'spaced', 'disabled' ]">{{ entryLocked ? 'Inventory' : 'Receive' }} {{ countSerialNumbers }} Item{{ countSerialNumbers == 1 ? '' : 's' }}</div>
			<div class="button neutral spaced" @click="clearData()">Start Again</div>
			<transition name="fade"><span class="message save-message" v-if="saveMessage">{{ saveMessage }}</span></transition>
		</div>
		<div v-if="saveComplete && showPrintControls" style="grid-column: 1 / span 2; margin: 0 0 0 1em; position: relative;">
			<div v-if="productSerialNumbers.length > 1" style="width: clamp(15rem, 5rem + 15vw, 20vw);">
				<p>Select a serial...</p>
				<select v-model="loadedIndex">
					<option v-for="(serialNumber, index) in productSerialNumbers" :value="index">{{ serialNumber }}</option>
				</select>
			</div>
			<tag-print :key="'tp_' + loadedIndex + product_detail.upc" 
						:productDetail="receiveWithoutUPC ? ffl_log_book_entry_ids[loadedIndex] : product_detail" 
						:serialNumbers="[productSerialNumbers[loadedIndex]]"
			/>
		</div>
		<div class="modal" v-if="showModal">
			<div class="inner">
				<h2>{{ $caplitalizeWords(modalFor) }}</h2>
				<div class="content">
					<div v-html="modalContent"></div>
					<div v-if="modalFor == 'cost_average'" class="checkbox-group">
						<input type="checkbox" :checked="inventory.cost_average" @click="setCostAveraging()">
						<label>Check to use cost averaging every time you receive an item. {{ modalMessage }}</label>
					</div>
				</div>
				<div class="controls">
					<div class="button cancel" @click="showModal = false">Got It</div>
				</div>
			</div>
		</div>

		<div v-if="showProductListModal" class="modal medium-modal" >
			<div class="inner">
				<h2>Item search</h2>
				<div class="content">
					<table>
						<tr v-for="product in productList" class="clickable-list-item" @click="assignProductFromSearch(product)">
							<td>{{ product.upc }}</td>
							<td>{{ product.description }}</td>
							<td>{{ product.model }}</td>
							<td>{{ product.caliber }}</td>
						</tr>
					</table>
					<div v-if="productList.length < 1" style="text-align: center; margin: 2em 0;">
						<div v-if="productSearchMessage" v-html="productSearchMessage" class="no-results"></div>
						<i v-if="searchingProducts" class="fa fa-spin fa-spinner"></i>
					</div>
				</div>
				<div class="controls">
					<div class="button cancel" @click="showProductListModal = false">Cancel</div>
				</div>
			</div>
		</div>

		<div v-if="showUPCListModal" class="modal medium-modal" >
			<div class="inner">
				<h2>Multiple Items Found</h2>
				<div class="content">
					<table>
						<tr v-for="product in productList" class="clickable-list-item" @click="assignUPCFromSearch(product)">
							<td>{{ product.upc }}</td>
							<td>{{ product.description }}</td>
							<td>{{ product.model }}</td>
							<td>{{ product.caliber }}</td>
						</tr>
					</table>
					<div v-if="productList.length < 1" style="text-align: center; margin: 2em 0;">
						<div v-if="productSearchMessage" v-html="productSearchMessage" class="no-results"></div>
						<i v-if="searchingProducts" class="fa fa-spin fa-spinner"></i>
					</div>
				</div>
				<div class="controls">
					<div class="button cancel" @click="showUPCListModal = false">Cancel</div>
				</div>
			</div>
		</div>

		<div v-if="showUPCNotFoundModal" class="modal">
	       <div class="inner">
	           <h2>UPC Not Found</h2>
	           <div class="content">
					<p>UPC <b>{{ product_detail.upc }}</b> was not found in store or distributor products. This firearm will be added to your logbooks, but not to your inventory for sale.</p>
					<p>If this is OK, you can proceed with receive. If you want to receive into inventory as well, create a new product with this UPC. You can also search again for a different UPC.</p>
	           </div>
	           <div class="controls">
	               <div class="button neutral spaced" @click="receiveWithoutUPC = true; showUPCNotFoundModal = false;">Proceed With Receive</div>
	               <div class="button neutral spaced" @click="editProduct()">Create New Product</div>
	               <div class="button neutral spaced" @click="showUPCNotFoundModal = false">Search Again</div>
	           </div>
	       </div>
	   </div>

		<div v-if="showModalCostPrice" class="modal">
	       <div class="inner">
	           <h2>Cost is greater than price</h2>
	           <div class="content">
	        		<p>You have entered a Cost of <b>${{ inventory.received_cost }}</b>, and a Price of <b>${{ inventory.price }}</b>.</p>
	        		<p>Click confirm if your cost really is higher than your price, otherwise click cancel and fix these values.</p>
	           </div>
	           <div class="controls">
	               <div class="button action spaced" @click="showModalCostPrice = false">Cancel</div>
	               <div class="button action spaced" @click="saveReceipt()">Confirm</div>
	           </div>
	       </div>
	   </div>

	</div>

	<div v-else-if="editingSupplier" class="detail three-one-columns">
		<div class="detail-form" style="position: relative;">
			<div style="position: absolute; top: 3px; right: 2.15em;">
				<label-required label="Supplier Type" :condition="contact.supplier_type_id"></label-required>
				<select style="width: 15em; height: 2.25em; font-size: 0.8em; padding: 0.2em 0.5em; margin: 0 0 0 0.5em;" v-model="contact.supplier_type_id">
					<option value="">Select...</option>
					<option v-for="supplier_type in supplier_types" :value="supplier_type.supplier_type_id">{{ supplier_type.description }}</option>
				</select>
			</div>
			<h2>Supplier Info</h2>
			<contact-detail-form :contact="contact" :clientId="client_id" :hideNotes="true" backToListLabel="Cancel" @save="assignSupplier($event)" @cancel="resetSearch()"></contact-detail-form>
		</div>
		<div class="detail-stats list">
			<h2>NOTE</h2>
			<div v-if="contact.contact_id">
				<p>Editing this contact record will not affect previously saved log book entries.  The contact will be updated for this and future receipt records.</p>
			</div>
			<div v-else>
				<p>You are adding a new contact record that can be used for this and future receipt records.</p>
			</div>
		</div>
	</div>

	<div v-else-if="editingProduct" class="detail two-columns">
		<div class="detail-form">
			<h2>Product Info</h2>
			<product-detail-form :product="product_detail" @save="assignProduct($event)" @cancel="cancelEditProduct()" :key="'product_detail_' + product_detail.product_id" />
		</div>
		<div class="detail-stats list">
			<h2>Inventory Note</h2>
			<p>Be careful to enter accurate product information.</p>
			<p v-if="product_detail.is_used">For used items, enter something in the UPC field that you can look up later.<br>We recommend 'U-' or 'C-'' followed by either the item's UPC or Serial Number</p>
			<p v-else-if="product_detail.no_upc">Even for items without a UPC, you should still enter something that you can look up later.<br>We recommend 'NUPC-' followed by five to seven unique digits.</p>
			<p v-else>If you're sure this product's UPC isn't already in the system, or that this product doesn't have a UPC, select a Deparment, Category, Manufacturer, and Caliber that best fit this product.</p>
		</div>
	</div>

	<loading v-else-if="loading"></loading>

	<message-modal v-if="message" :message="message" :loading="loading || saving" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

	<help-popup :helpLink="{187:'Receive', 188:'Receive a Firearm Using UPC', 189:'Receive a Firearm With a Custom UPC', 190:'Receive a New Firearm With No UPC', 191:'Receive a Used Firearm', 191:'Receive a Used Firearm'}" :tours="false"></help-popup>

</div>

</template>

<script>
import FFLNumberSearch from "@/components/FFLNumberSearch.vue"
import LabelRequired from '@/components/LabelRequired.vue'
import ContactDetailForm from '@/components/ContactDetailForm.vue'
import TagPrint from '@/components/TagPrint.vue'
import ProductDetailMinimum from '@/components/ProductDetailMinimum.vue'
import DateInput from '@/components/DateInput.vue'
import HelpPopup from '@/components/HelpPopup.vue'
import InputValidation from '@/components/InputValidation.vue'

export default {
	name: 'logbooks-receive',
    components: {
		'ffl-number-search': FFLNumberSearch,
        'label-required': LabelRequired,
        'contact-detail-form': ContactDetailForm,
        'tag-print': TagPrint,
        'product-detail-form': ProductDetailMinimum,
		'date-input': DateInput,
		'help-popup': HelpPopup, 
		'input-validation' : InputValidation
    },
	data() {
		return {
            loading: false,
			loadedIndex: 0,
            loadingSuppliers: false,
            upcSearching: false,
			saving: false,
			message: '',
			messageStatus: '',
			messageTimeout: 0,
			client_id: null,
    		logbooks: [],
    		gun_types: [],
			fflNumberInfo: {},
			supplier_types: [],
			fflTransactionInfoRequired: false,
            gunTypesRequired: [],
			employee_name: '',
    		entry: {},
    		is_transfer: false,
    		inventory: {},
    		product_detail: {},
    		serial_numbers: [{ serial_number: '', finish: '' }],
			productSerialNumbers: [],
			sequence_codes: [''],
    		search_suppliers: '',
    		supplierSearchMessage: null,
			supplierSearchContext: 'suppliers',
    		suppliers: [],
			showFFLSearch: false,
    		contact: {},
    		editingSupplier: false,
    		upcSearchMessage: null,
			product_search: '',
			productList: [],
			showProductListModal: false,
			showUPCListModal: false,
			searchingProducts: false,
			productSearchMessage: '',
    		editingProduct: false,
    		saveMessage: '',
    		showModal: false,
    		modalFor: '',
    		modalContent: '',
    		modalMessage: '',
			saving: false,
    		saveComplete: false,
			showPrintControls: false,
			confirmDuplicatesMessage: '',
            duplicatesOK: false,
			showModalCostPrice: false,
			showUPCNotFoundModal: false,
			receiveWithoutUPC: false,
			ffl_log_book_entry_ids: [],
			reloadDateInput: Math.random() // will re-render component on clearData()
		}
	},
    created() {
        this.loading = true
		this.apiCall({ destination: 'get_log_book_control_lists' }).then(obj => {
			this.resetEntry()
			this.resetContact()
			this.resetInventory()
            this.loading = false
			if (obj.status == 'success') {
				this.client_id = obj.client_id
				this.logbooks = obj.logbooks
				this.gun_types = obj.gun_types
                if (obj.required_gun_types) {
                    this.gunTypesRequired = obj.required_gun_types
                }
				this.fflNumberInfo = obj.ffl_number_info
				this.fflTransactionInfoRequired = obj.firearms_transaction_info_required
				this.employee_name = this.entry.employee_name = this.$fullName(obj.user_contact)
				if (this.$route.query.id) {
					this.getItem(this.$route.query.id)
				}
				if (this.$route.query.ffl_log_book_entry_id) {
					this.getEntry(this.$route.query.ffl_log_book_entry_id)
				}
			}
		}).catch(err => {
            this.loading = false
			console.log(err)
		})
        this.loading = true
		this.apiCall({ destination: 'get_list_supplier_types' }).then(obj => {
            this.loading = false
			if (obj.status == 'success') {
				this.supplier_types = obj.supplier_types
			}
		}).catch(err => {
            this.loading = false
			console.log(err)
		})
    },
    watch: {
		saveMessage(newVal) {
			if (newVal) {
				setTimeout(() => {
					this.saveMessage = null
				}, 3000)
			}
		}
	},
    computed: {
	    sectionsComplete() {
			let complete = 0
			if (this.entry.ffl_log_book_id && this.notEmpty(this.entry,['ffl_log_book_id', 'receipt_date']) && (this.contact.state || this.contact.country_code) && this.notEmpty(this.contact,['address_1', 'city', 'zip_code'])) {
				if (this.entry.ffl_transaction_type_id) {
					if (this.notEmpty(this.entry,['employee_name'])) {
						complete = 1
					}
				} else {
					complete = 1
				}
			}
			if (complete == 1) {
				if (this.is_transfer) {
					complete = 2
				}
				if ((this.product_detail.no_upc || this.notEmpty(this.product_detail,['upc'])) && this.notEmpty(this.inventory,['received_cost', 'price', 'web_price'])) {
					complete = 2
				}
				if (this.receiveWithoutUPC) {
					complete = 2
				}
			}
			if (complete == 2) {
				if (this.notEmpty(this.entry,['manufacturer', 'gun_type', 'caliber', 'model'])) {
					complete = 3
				}
			}
		    if (complete == 3) {
			    if (this.serial_numbers[0].serial_number.length > 0) {
					if (this.fflTransactionInfoRequired && this.entry.transaction_info_required && this.gunTypesRequired.includes(this.entry['gun_type'])) {
						// all serial numbers must have a finish
						for (let i = 0; i < this.serial_numbers.length; i++) {
							if (!this.serial_numbers[i]['finish']) {
								return complete
							} else if (this.serial_numbers[i]['finish'] == 'two_toned' && (!this.serial_numbers[i].finish_two_toned_1 || !this.serial_numbers[i].finish_two_toned_2)) {
								return complete
							} else if (this.serial_numbers[i]['finish'] == 'cerakote' && !this.serial_numbers[i].finish_cerakote) {
								return complete
							} else if (this.serial_numbers[i]['finish'] == 'other' && !this.serial_numbers[i].finish_other) {
								return complete
							} else {
								complete = 4
							}
						}
					} else {
						complete = 4
					}
				}
			}
			return complete
		},
		allowEditProduct() {
			return this.product_detail.no_upc || this.entry.is_used || this.product_detail.client_id == this.getClient.client_id || this.upcSearchMessage
		},
		productButtonLabel() {
			return this.product_detail.client_id == this.client_id ? 'Edit' : 'Create'
		},
		entryLocked() {
            return this.$route.query.ffl_log_book_entry_id || this.saveComplete
        },
		saveDisabled() {
            if (this.sectionsComplete < 4) {
                return true
            }
            if (this.confirmDuplicatesMessage && !this.duplicatesOK) {
                return true
            }
            if (this.saving || this.saveComplete) {
                return true
            }
        },
		today() {
			return new Date().toISOString().split('T')[0];
		},
		countSerialNumbers() {
			return this.serial_numbers.filter(sn => sn.serial_number.length > 0).length
		}
	},
	methods: {
		toggleNoUpc() {
			this.product_detail.no_upc = !this.product_detail.no_upc
			if (this.product_detail.no_upc) {
				this.editProduct()
			}
		},
		toggleIsUsed() {
			this.entry.is_used = !this.entry.is_used
			if (this.entry.is_used) {
				this.product_detail.is_used = true
				if (!this.is_transfer) {
					this.editProduct()
				}
			} else {
				this.product_detail.is_used = false
			}
		},
		notEmpty(object, elements) {
			for (var i in elements) {
				if (typeof object[elements[i]] == 'undefined' || object[elements[i]] == null || object[elements[i]] == "") {
					return false
				}
			}
			return true
		},
		formatCurrency(numberString) {
			numberString = parseFloat(numberString).toFixed(2)
		},
		recalculateProfit(which) {
			let useCost = parseFloat(this.inventory.received_cost).toFixed(2) || parseFloat(this.inventory.cost).toFixed(2)
			if (useCost) {
				switch (which) {
					case 'received_cost':
						if (this.inventory.price) {
							this.inventory.price = parseFloat(this.inventory.price).toFixed(2)
							this.inventory.pos_profit = (100 * (1 - (useCost / this.inventory.price))).toFixed(1)
						} else if (this.inventory.pos_profit) {
							this.inventory.pos_profit = parseFloat(this.inventory.pos_profit).toFixed(1)
							this.inventory.price = Math.round(parseFloat(useCost) / (1 - (parseFloat(this.inventory.pos_profit) / 100)), 2).toFixed(2)
						}
						if (this.inventory.web_price) {
							this.inventory.web_price = parseFloat(this.inventory.web_price).toFixed(2)
							this.inventory.web_profit = (100 * (1 - (useCost / this.inventory.web_price))).toFixed(1)
						} else if (this.inventory.web_profit) {
							this.inventory.web_profit = parseFloat(this.inventory.web_profit).toFixed(1)
							this.inventory.web_price = Math.round(parseFloat(useCost) / (1 - (parseFloat(this.inventory.web_profit) / 100)), 2).toFixed(2)
						}
						this.inventory.received_cost = parseFloat(this.inventory.received_cost).toFixed(2)
						break;
					case 'price':
						if (this.inventory.price) {
							this.inventory.price = parseFloat(this.inventory.price).toFixed(2)
							this.inventory.pos_profit = (100 * (1 - (useCost / this.inventory.price))).toFixed(1)
						}
						break
					case 'pos_profit':
						if (this.inventory.pos_profit) {
							this.inventory.pos_profit = parseFloat(this.inventory.pos_profit).toFixed(1)
							this.inventory.price = (parseFloat(useCost) / (1 - (parseFloat(this.inventory.pos_profit) / 100))).toFixed(2)
						}
						break
					case 'web_price':
						if (this.inventory.web_price) {
							this.inventory.web_price = parseFloat(this.inventory.web_price).toFixed(2)
							this.inventory.web_profit = (100 * (1 - (useCost / this.inventory.web_price))).toFixed(1)
						}
						break
					case 'web_profit':
						if (this.inventory.web_profit) {
							this.inventory.web_profit = parseFloat(this.inventory.web_profit).toFixed(1)
							this.inventory.web_price = (parseFloat(useCost) / (1 - (parseFloat(this.inventory.web_profit) / 100))).toFixed(2)
						}
						break
				}

			} else {
				console.log('no cost basis')
			}

		},
		addSerialInput() {
			if (this.serial_numbers[this.serial_numbers.length - 1]['serial_number'] != '') {
				this.serial_numbers.push({ serial_number: '', finish: '' })
				// use ref to set focus to new serial input after DOM updates, allows for rapid serial scanning
				this.$nextTick(() => {
					const newIndex = this.serial_numbers.length - 1
					const ref = this.$refs['serial_input_' + newIndex]
					if (ref && ref[0]) {
						ref[0].focus()
					}
				})
			}
		},
		removeSerialInput() {
			if (this.serial_numbers.length > 1) {
				this.serial_numbers.pop()
			} else {
				this.serial_numbers.splice(0, 1, { serial_number: '', finish: '' })
			}
		},
		searchSuppliers(forceContext) {
            this.loadingSuppliers = true
			this.suppliers = []
			this.supplierSearchMessage = null
			if (forceContext) {
				this.supplierSearchContext = forceContext
			}
			this.apiCall({ destination: 'get_list_suppliers', data: { search_term: this.search_suppliers, context: this.supplierSearchContext, transaction_info: this.fflTransactionInfoRequired }}).then(obj => {
                this.loadingSuppliers = false
				if (obj.status == 'error') {
					this.supplierSearchMessage = obj.message
					this.supplierSearchContext = 'suppliers' // reset for next search
				} else if (obj.suppliers.length > 1) {
					this.suppliers = obj.suppliers
				} else if (obj.suppliers.length == 1) {
					this.assignSupplier(obj.suppliers[0])
				} else {
					this.supplierSearchMessage = 'No matching ' + this.supplierSearchContext + ' found.'
				}
			})
		},
		searchCustomers() {
			this.supplierSearchContext = 'customers'
			this.searchSuppliers()
		},
		setList(list) {
			if (list.length > 1) {
				this.suppliers = list
			} else if (list.length == 1) {
				this.apiCall({ destination: 'get_supplier_from_ffl_search', data: list[0] }).then(obj => {
					if (obj.status == 'success') {
						this.assignSupplier(obj.supplier_detail)
					} else {
						this.supplierSearchMessage = obj.message
					}
				}).catch(err => {
					console.log(err)
				})
			} else {
				this.supplierSearchMessage = 'No matching FFL found.'
			}
		},
		resetEntry() {
    		this.entry = {
	    		receipt_date: '',
	    		ffl_log_book_id: this.$route.query.log_book || '',
	    		disposal_notes: null,
				is_used: false,
				manufacturer: '',
				importer: '',
				country_of_manufacture: '',
				model: '',
				gun_type: '',
				caliber: '',
				finish: '',
				transaction_info_required: true,
				employee_name: this.employee_name
			}
			this.reloadDateInput = Math.random()
		},
		resetContact() {
			this.contact = {
				client_id: '',
				contact_id: '',
				first_name: '',
				last_name: '',
				company_name: '',
				address_1: '',
				address_2: '',
				city: '',
				state: '',
				zip_code: '',
				ffl_number: '',
				supplier_type_id: '',
				date_of_birth: '',
				occupation: '',
				notes: [{ note: '', priority: '' }]
			}
		},
		resetSearch() {
			this.editingSupplier = false
			this.resetContact()
			this.supplierSearchContext = 'suppliers' // reset for next search
			this.search_suppliers = ''
		},
		resetInventory() {
    		this.inventory = {
				client_id: '',
	    		product_id: '',
	    		description: '',
	    		upc: '',
	    		cost: '',
	    		price: '',
	    		on_hand: '',
	    		available: '',
	    		cost_average: ''
	    	}
			if (!this.entry.manufacturer && !this.entryLocked) {
				this.entry.manufacturer = ''
				this.entry.importer = ''
				this.entry.country_of_manufacture = ''
				this.entry.model = ''
				this.entry.gun_type = ''
				this.entry.caliber = ''
			}
			this.product_detail = {
				no_upc: ''
			}
		},
		newSupplier() {
			this.suppliers = []
			this.supplierSearchMessage = ''
			this.supplierSearchContext = 'suppliers'
			this.resetContact()
			this.contact.client_id = this.client_id
			this.editingSupplier = true
		},
		editProduct() {
			this.showUPCNotFoundModal = false
			if (this.allowEditProduct) {
				if (!this.product_detail.client_id) {
					this.product_detail.new_product = true
					this.product_detail.client_id = this.client_id
					this.product_detail.department_id = ''
					this.product_detail.category_id = ''
					this.product_detail.product_type_id = ''
				}
				this.upcSearchMessage = null
				this.editingProduct = true
			}
		},
		cancelEditProduct() {
			this.product_detail = {
				no_upc: '',
				is_used: ''
			}
			this.entry.is_used = ''
			this.editingProduct = false
		},
		assignSupplier(supplier) {
			if (this.editingSupplier && !this.contact.supplier_type_id) {
				return false
			}
			if (this.supplierSearchContext == 'customers') {
				this.contact = supplier
				this.suppliers = []
				this.supplierSearchMessage = ''
				this.editingSupplier = true
				this.supplierSearchContext = 'suppliers' // reset for next search
				return false
			}
			if (this.contact.supplier_type_id) {
				supplier.supplier_type_id = this.contact.supplier_type_id
			}
			this.contact = supplier
			this.contact.date_of_birth = supplier.birthdate // normalize birthdate to date_of_birth
			if (!this.contact.full_name) {
					this.contact.full_name = this.$fullName(this.contact)
			}
			this.suppliers = []
			this.search_suppliers = null
			this.editingSupplier = false
		},
		assignProduct(product) {
			this.product_detail = product
			this.inventory.upc = product.upc
			if (!this.entryLocked) {
				this.entry.manufacturer = product.manufacturer
				this.entry.model = product.model
				this.entry.gun_type = product.gun_type
				this.entry.caliber = product.caliber
			}
			this.editingProduct = false
		},
		assignProductFromSearch(product) {
			this.showProductListModal = false
			this.product_search = product.upc
			this.product_detail.upc = product.upc
			this.getItem()
		},
		assignUPCFromSearch(product) {
			this.showUPCListModal = false
			this.getProductInventory(product.product_id)
			this.product_detail = product
			if (!this.entryLocked) {
				this.entry.manufacturer_id = product.manufacturer_id
				this.entry.manufacturer = product.manufacturer
				this.entry.importer = product.importer
				this.entry.country_of_manufacture = product.country_of_manufacture
				this.entry.model = product.model
				this.entry.gun_type = product.gun_type
				this.entry.caliber_id = product.caliber_id
				this.entry.caliber = product.caliber
			}
		},
		getItem(productSearch) {
			let productId = ''
			let searchTerm = ''
			let searchUPC = ''
			let apiAction = ''
			this.receiveWithoutUPC = false
			// if passed productSearch is the route id, then we're getting a specific product
			if (productSearch && productSearch == this.$route.query.id) {
				productId = productSearch
				apiAction = 'get_item_product_by_id'
			} else if (this.product_detail.upc) {
				searchUPC = this.product_detail.upc
				apiAction = 'get_item_product_by_upc'
				this.upcSearching = false
			} else if (this.product_search) {
				searchTerm = this.product_search
				apiAction = 'get_list_products'
				this.productList = []
				this.productSearchMessage = 'Searching for ' + this.product_search
				this.searchingProducts = true
				this.showProductListModal = true
			}
			this.resetInventory()
			this.product_detail.upc = searchUPC
			this.upcSearchMessage = null
			this.upcSearching = true
			this.apiCall({ destination: apiAction, data: { product_id: productId, search_term: searchTerm, upc: searchUPC, contact_id: this.contact.contact_id, all_products: 1 } }).then(obj => {
				this.upcSearching = false
				this.searchingProducts = false
				if (obj.status == 'success') {
					if (apiAction == 'get_list_products') {
						if (obj.list.length == 0) {
							this.productSearchMessage = 'No items matching items found.<br>You can cancel and try searching again.'
						}
						if (obj.list.length == 1) {
							obj.product_detail = obj.list[0]
							this.showProductListModal = false
						}
						if (obj.list.length > 1) {
							this.productList = obj.list
							this.showProductListModal = true
						}
					}
					if (apiAction == 'get_item_product_by_upc' && obj.product_list.length > 1) {
						this.productList = obj.product_list
						this.showUPCListModal = true
					}
					if ('product_detail' in obj && obj.product_detail.product_id) {
						this.getProductInventory(obj.product_detail.product_id)
						this.product_detail = obj.product_detail
						if (!this.entryLocked) {
							this.entry.manufacturer_id = obj.product_detail.manufacturer_id
							this.entry.manufacturer = obj.product_detail.manufacturer
							this.entry.importer = obj.product_detail.importer
							this.entry.country_of_manufacture = obj.product_detail.country_of_manufacture
							this.entry.model = obj.product_detail.model
							this.entry.gun_type = obj.product_detail.gun_type
							this.entry.caliber_id = obj.product_detail.caliber_id
							this.entry.caliber = obj.product_detail.caliber
						}
					}
				} else {
					if (apiAction == 'get_item_product_by_upc') {
						this.upcSearchMessage = obj.message
						this.showUPCNotFoundModal = true
					}
				}
			}).catch(err => {
				this.upcSearching = false
				console.log(err)
			})
		},
		getProductInventory(productId) {
			this.apiCall({ destination: 'get_item_product_inventory', data: { product_id: productId } }).then(obj => {
				if (obj.status == 'success') {
					this.inventory = obj.inventory
				} else {
					this.upcSearchMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		setCostAveraging() {
			this.apiCall({ destination: 'save_item_preference', data: { preference_code: 'DEFAULT_COST_AVERAGING', preference_type: 'client', value: 1 } }).then(obj => {
				this.modalMessage = obj.message
				this.inventory.cost_average = true
			}).catch(err => {
				this.upcSearching = false
				console.log(err)
			})
		},
		getEntry(fflLogBookEntryId) {
			this.apiCall({ destination: 'get_item_ffl_log_book_entry', data: { ffl_log_book_entry_id: fflLogBookEntryId } }).then(obj => {
				if (obj.status == 'success') {
					// fist word of originator for supplier search
					let supplierNameParts = obj.entry.originator_full_name.split(' ')
					this.search_suppliers = supplierNameParts[0]
					this.searchSuppliers()
					this.product_search = obj.entry.manufacturer + ' ' + obj.entry.model
					this.getItem()
                    this.entry.ffl_log_book_id = obj.entry.ffl_log_book_id
					this.entry.manufacturer = obj.entry.manufacturer
					this.entry.importer = obj.entry.importer
					this.entry.country_of_manufacture = obj.entry.country_of_manufacture
					this.entry.model = obj.entry.model
					this.entry.gun_type = obj.entry.gun_type
					this.entry.caliber = obj.entry.caliber
                    this.serial_numbers[0] = { serial_number: obj.entry.serial_number, finish: '' }
				}
			}).catch(err => {
				console.log(err)
			})
		},
		confirmDuplicates() {
			if (this.saveDisabled) {
                return false
            }
			this.saving = true
            // confirm duplicate serial numbers and if present allow save only if user acknowleges
            if (this.duplicatesOK || this.is_transfer || this.entryLocked) {
				this.saving = false
                this.confirmCostPrice()
            } else {
                this.confirmDuplicatesMessage = ''
                let duplicateCount = {}
                for (var i = 0; i < this.serial_numbers.length; i++) {
                    if (this.serial_numbers[i]['serial_number'] == '') { continue }
                    if (!duplicateCount[this.serial_numbers[i]['serial_number']]) {
                        duplicateCount[this.serial_numbers[i]['serial_number']] = 0
                    }
                    duplicateCount[this.serial_numbers[i]['serial_number']]++
                }
                for (let [key, value] of Object.entries(duplicateCount)) {
                    if (value > 1) {
                        this.confirmDuplicatesMessage = 'You have entered duplicate serial numbers.'
						this.saving = false
                        return false
                    }
                }
                // no dupes in this entry, so look for already entered SNs in database
                this.apiCall({ destination: 'confirm_duplicate_serial_numbers', data: { ffl_log_book_id: this.entry.ffl_log_book_id, serial_numbers: this.serial_numbers } }).then(obj => {
                    if (obj.on_hand_serial_numbers.length > 0) {
                        this.confirmDuplicatesMessage = 'Serial number' + (obj.on_hand_serial_numbers.length > 1 ? 's ' : ' ') + obj.on_hand_serial_numbers.map(sn => sn.serial_number).join(', ') + ' already in your log books.'
						this.saving = false
                    } else {
						this.saving = false
                        this.confirmCostPrice()
                    }
                }).catch(err => {
    				console.log(err)
    			})
            }
        },
        confirmCostPrice() {
            if (parseFloat(this.inventory.received_cost) > parseFloat(this.inventory.price)) {
                this.showModalCostPrice = true
                return false
            } else {
                this.saveReceipt()
            }
        },
		saveReceipt() {
            this.showModalCostPrice = false
			if (this.sectionsComplete < 4 || this.saveComplete || this.saving) {
				return
			}

			this.message = 'Receiving...'
			this.messageStatus = ''

			// only post non-blank serial numbers
			let postSerialNumbers = []
			for (let i = 0; i < this.serial_numbers.length; i++) {
				if (this.serial_numbers[i]['serial_number']) {
					postSerialNumbers.push(this.serial_numbers[i])
				} else {
					// remove this element
					this.serial_numbers.splice(i, 1)
					i-- // adjust index since we removed an element
				}
			}
			if (postSerialNumbers.length < 1) {
				this.message = 'There are no valid serial numbers.'
				this.messageStatus = 'error'
				return false
			}
			if (!this.product_detail.upc || (this.product_detail.upc && this.product_detail.upc.length === 0)) {
				this.receiveWithoutUPC = true
			}
			this.message = 'Saving receipt...'
			this.messageStatus = ''
			this.saving = true
			this.apiCall({ destination: 'save_item_ffl_log_book_entry', data: { entry: this.entry, contact: this.contact, serial_numbers: postSerialNumbers, inventory: this.inventory, product_detail: this.product_detail, is_transfer: this.is_transfer, attach_inventory_to_ffl_log_book_entry_id: this.$route.query.ffl_log_book_entry_id } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {

					// populate productSerialNumbers with the serial numbers that were saved
					this.productSerialNumbers = []
					for (let i = 0; i < postSerialNumbers.length; i++) {
						this.productSerialNumbers.push(postSerialNumbers[i].serial_number)
					}

					if ('sequence_codes' in obj) {
						this.sequence_codes = obj.sequence_codes
					}

					if (this.fflTransactionInfoRequired && this.entry.transaction_info_required) {
						this.message = 'Saving additional transaction info...'
						let fflLogBookEntryIds = {}
						if ('ffl_log_book_entry_ids' in obj) {
							fflLogBookEntryIds = obj.ffl_log_book_entry_ids
						}
						// transaction records require additional information
						for (let i = 0; i < postSerialNumbers.length; i++) {
							postSerialNumbers[i].caliber = this.entry.caliber
							postSerialNumbers[i].gun_type = this.entry.gun_type
							postSerialNumbers[i].manufacturer = this.entry.manufacturer
							postSerialNumbers[i].model = this.entry.model
							postSerialNumbers[i].ffl_log_book_entry_id = fflLogBookEntryIds[postSerialNumbers[i].serial_number]
						}
						this.apiCall({ destination: 'save_item_ffl_transaction_record', data: { is_receipt: true, entry: this.entry, contact: this.contact, serial_numbers: postSerialNumbers } }).then(obj => {
							this.messageStatus = obj.status
							if (obj.status == 'success') {
								this.saving = false
								this.message = "Success! " + obj.message
								this.messageTimeout = 2000
								this.dispositionComplete = true
							} else {
								this.message = "Error: " + obj.message
								this.messageTimeout = 0
							}
						}).catch(err => {
							console.log(err)
						})

					} else {
						this.message = obj.message
						this.messageTimeout = 2000
					}

					// force product_id to what was actually saved in the API, mostly for print tags
					this.product_detail.product_id = obj.product_id
					this.product_detail.ffl_log_book_entry_id = obj.ffl_log_book_entry_id
					if (!this.product_detail.product_id) {
						this.receiveWithoutUPC = true
					}
					this.ffl_log_book_entry_ids = postSerialNumbers.map(sn => ({ ffl_log_book_entry_id: obj.ffl_log_book_entry_ids[sn.serial_number]}));
					this.product_detail.entryBySerial = obj.ffl_log_book_entry_ids
					this.product_detail.receiving_non_inventory = this.is_transfer
					this.saveComplete = true
					this.showPrintControls = true
				} else {
					this.message = "Error: " + obj.message
				}
			}).catch(err => {
				this.saving = false
				console.log(err)
			})
		},
		clearData() {
	    	this.resetEntry()
	    	this.resetContact()
	    	this.resetInventory()
	    	this.serial_numbers = [{ serial_number: '', finish: '' }]
			this.confirmDuplicatesMessage = ''
            this.duplicatesOK = false
	    	this.is_transfer = false
	    	this.showPrintControls = false
			this.saving = false
	    	this.saveComplete = false
			// return to top of screen
			document.getElementById('detail_receive').scrollTop = 0
		},
		showTip(which) {
			switch (which) {
				case 'cost_average':
		    		this.modalFor = 'Cost Averaging'
					this.modalContent  = "<p>Cost averaging will calculate your new cost as the average of your previously recorded cost and this item's received cost.</p>"
					this.modalContent += "<p>We'll take into account how many you still have on hand at the current cost, and how many you are receiving at this cost.</p>"
					break
				case 'auto_order':
		    		this.modalFor = 'Automatic Reordering'
					this.modalContent  = "<p>With automatic reordering set to 'Yes', your system will generate an electronic order when your on hand quantity is less than or equal to the reorder quantity. "
					this.modalContent += "Your distributor preference and distributor availability will be taken into account.</p>"
					this.modalContent += "<p>With automatic reordering set to 'No', this item will be added to your dealer shopping cart for manal reordering when your on hand quantity is less than or equal to the reorder quantity.</p>"
					break
			}
			this.showModal = true
		}
	}
}
</script>

<style lang="scss" scoped>
.section_dead {
	position: absolute;
	top: 0; left: 0;
	width: 100%; height: 100%;
	background-color: rgba(255, 255, 255, .3);
	cursor: not-allowed;
}
.complete-first {
	text-transform: none;
	font-size: 0.8em;
	font-weight: normal;
	font-style: italic;
	padding: 0 0 0 0.5em;
}
// #section_2 {
// 	order: 
// }
</style>
